import { hubMessage } from '../entities/chatHub-message';
import { Message } from '../types/chat.types';

const STORAGE_KEY = 'virtco_crossViewHubMessages';
const MAX_PER_CONTACT = 40;

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export function isEmptyGuid(id: string | null | undefined): boolean {
  if (id == null || String(id).trim() === '') return true;
  return normalizeContactIdForQueue(id) === normalizeContactIdForQueue(EMPTY_GUID);
}

/**
 * True when a row belongs to a 1:1 private thread between currentUserId and peerId.
 * Outgoing rows with empty recipient are only trusted when trustEmptyRecipientOutgoing is set
 * (e.g. GetMessageData scoped to peerId); otherwise they are excluded from cross-thread merges.
 */
export function messageBelongsToPrivateThread(
  message: { creatorId?: string; recipientUserId?: string; messageRecipientId?: string },
  peerId: string,
  currentUserId: string | null | undefined,
  options?: { trustEmptyRecipientOutgoing?: boolean }
): boolean {
  const peerNorm = normalizeContactIdForQueue(peerId);
  const userNorm = normalizeContactIdForQueue(currentUserId);
  if (!peerNorm || !userNorm) return false;

  const creatNorm = normalizeContactIdForQueue(message.creatorId);
  if (creatNorm !== peerNorm && creatNorm !== userNorm) return false;

  const recipientRaw = message.recipientUserId || message.messageRecipientId;
  if (!isEmptyGuid(recipientRaw)) {
    const recipNorm = normalizeContactIdForQueue(recipientRaw);
    if (recipNorm !== peerNorm && recipNorm !== userNorm) return false;
    return true;
  }

  if (creatNorm === peerNorm) {
    return true;
  }

  if (creatNorm === userNorm) {
    return !!options?.trustEmptyRecipientOutgoing;
  }

  return false;
}

export function filterMessagesForPrivateThread(
  messages: Message[],
  peerId: string,
  currentUserId: string | null | undefined,
  options?: { trustEmptyRecipientOutgoing?: boolean }
): Message[] {
  return messages.filter((msg) =>
    messageBelongsToPrivateThread(
      {
        creatorId: msg.creatorId,
        recipientUserId: msg.recipientUserId,
        messageRecipientId: (msg as Message & { messageRecipientId?: string }).messageRecipientId,
      },
      peerId,
      currentUserId,
      options
    )
  );
}

/** Resolve the other participant in a private hub/API message for the logged-in user. */
export function resolvePrivateThreadPeerId(
  message: { creatorId?: string; messageRecipientId?: string; recipientUserId?: string },
  currentUserId: string | null | undefined
): string {
  const userNorm = normalizeContactIdForQueue(currentUserId);
  if (!userNorm) return '';

  const recipientRaw = message.messageRecipientId || message.recipientUserId;
  const recipNorm = normalizeContactIdForQueue(recipientRaw);
  const creatNorm = normalizeContactIdForQueue(message.creatorId);

  if (recipNorm === userNorm) {
    return message.creatorId || '';
  }
  if (creatNorm === userNorm && !isEmptyGuid(recipientRaw)) {
    return recipientRaw || '';
  }
  // Hub sometimes omits recipient on inbound DMs — peer is the creator
  if (creatNorm && creatNorm !== userNorm && isEmptyGuid(recipientRaw)) {
    return message.creatorId || '';
  }
  return '';
}

/** localStorage: normalized peer id when /messages (ChatMain) has a thread open — header + unread sync */
export const ACTIVE_CHAT_MAIN_RECIPIENT_KEY = 'activeChatMainRecipientId';

/** localStorage: normalized peer id when project discussion chat is open */
export const ACTIVE_PROJECT_CHAT_RECIPIENT_KEY = 'activeProjectChatRecipientId';

/**
 * One canonical id for queue + open-conversation lookups.
 * Main chat list, project chat, and SignalR payloads often disagree on GUID casing.
 */
export function normalizeContactIdForQueue(contactId: string | null | undefined): string {
  if (contactId == null || contactId === '') return '';
  return String(contactId).trim().toLowerCase();
}

/** `/messages` or `/messages/{contactId}` — contact id survives refresh. */
export function getMessagesContactIdFromPath(pathname: string): string | null {
  const path = (pathname || '/').split('?')[0] || '/';
  const normalized =
    path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  if (normalized === '/messages') return null;
  if (!normalized.startsWith('/messages/')) return null;

  const segment = normalized.slice('/messages/'.length);
  if (!segment || segment.includes('/')) return null;

  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Supported list filters from email / deep links: `?filter=unread`. */
export function getMessagesListFilterFromSearch(search: string): string | null {
  try {
    const params = new URLSearchParams(search || '');
    const filter = (params.get('filter') || '').trim().toLowerCase();
    if (filter === 'unread' || filter === 'favourites' || filter === 'favorites' || filter === 'all') {
      return filter === 'favorites' ? 'favourites' : filter;
    }
  } catch {
    // ignore malformed query
  }
  return null;
}

export function buildMessagesPath(
  contactId?: string | null,
  options?: { filter?: string | null }
): string {
  const id = (contactId || '').trim();
  const base = !id ? '/messages' : `/messages/${encodeURIComponent(id)}`;
  const filter = (options?.filter || '').trim().toLowerCase();
  if (!filter || filter === 'all') return base;
  const normalized = filter === 'favorites' ? 'favourites' : filter;
  return `${base}?filter=${encodeURIComponent(normalized)}`;
}

export function isSameMessagesContact(
  a?: string | null,
  b?: string | null
): boolean {
  const left = normalizeContactIdForQueue(a);
  const right = normalizeContactIdForQueue(b);
  return !!left && left === right;
}

export function normalizeHubMsgId(message: hubMessage | undefined): string {
  if (!message) return '';
  const m = message as hubMessage & { MsgId?: string; Id?: string };
  const id =
    message.msgId ||
    m.MsgId ||
    (message as { id?: string }).id ||
    m.Id ||
    '';
  return id ? String(id) : '';
}

/**
 * SignalR server handlers sometimes send a single arg as [obj], or wrap the DTO in data/Message.
 */
export function unwrapHubMessageRaw(raw: unknown): unknown {
  if (raw == null) return raw;
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object' && raw[0] !== null) {
    return raw[0];
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const inner =
      o.data ??
      o.Data ??
      o.message ??
      o.Message ??
      o.payload ??
      o.Payload ??
      o.result ??
      o.Result;
    if (inner != null && typeof inner === 'object') {
      return inner;
    }
  }
  return raw;
}

/**
 * Hub payloads sometimes arrive with PascalCase (.NET default) while the UI expects camelCase.
 * Returns a plain object safe to use as hubMessage for routing and mapping.
 */
export function coerceHubMessageShape(raw: unknown): hubMessage {
  const unwrapped = unwrapHubMessageRaw(raw);
  const r = unwrapped as Record<string, unknown>;
  const pick = (c: string, p: string): string => {
    const a = r[c];
    const b = r[p];
    const v = a !== undefined && a !== null ? a : b;
    if (v === undefined || v === null) return '';
    return String(v);
  };
  const msgId =
    pick('msgId', 'MsgId') ||
    pick('id', 'Id') ||
    '';
  let creatorId = pick('creatorId', 'CreatorId');
  if (!creatorId) {
    creatorId =
      pick('senderId', 'SenderId') ||
      pick('fromUserId', 'FromUserId') ||
      pick('senderUserId', 'SenderUserId');
  }
  let messageRecipientId = pick('messageRecipientId', 'MessageRecipientId');
  if (!messageRecipientId) {
    messageRecipientId =
      pick('receiverId', 'ReceiverId') ||
      pick('toUserId', 'ToUserId') ||
      pick('recipientId', 'RecipientId') ||
      pick('recipientUserId', 'RecipientUserId');
  }
  return {
    msgId,
    parentMessageId: pick('parentMessageId', 'ParentMessageId'),
    creatorId,
    type: pick('type', 'Type'),
    messageBody: pick('messageBody', 'MessageBody'),
    docData: pick('docData', 'DocData'),
    createdDate: pick('createdDate', 'CreatedDate'),
    sendToGroup: Boolean(r.sendToGroup ?? r.SendToGroup ?? false),
    messageRecipientId,
    parentMsgDta: (r.parentMsgDta ?? r.ParentMsgDta) as hubMessage['parentMsgDta'],
    userName: pick('userName', 'UserName'),
    email: pick('email', 'Email'),
    firstName: pick('firstName', 'FirstName'),
    lastName: pick('lastName', 'LastName'),
    userAvatar: pick('userAvatar', 'UserAvatar'),
  } as hubMessage;
}

/**
 * When /messages (ChatMain) is unmounted, useSignalRMessages does not run — only project chat receives.
 * Queue hub payloads here so the other view can merge them after GetMessageData.
 */
export function queueHubMessageForCrossViewSync(contactId: string, message: hubMessage): void {
  const normId = normalizeContactIdForQueue(contactId);
  if (!normId || !message) return;
  const msgId = normalizeHubMsgId(message);
  if (!msgId) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const data: Record<string, hubMessage[]> = raw ? JSON.parse(raw) : {};
    const list = data[normId] || [];
    if (list.some((m) => normalizeHubMsgId(m) === msgId)) {
      return;
    }
    list.push(message);
    data[normId] = list.slice(-MAX_PER_CONTACT);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / JSON */
  }
}

export function takeQueuedHubMessagesForContact(contactId: string): hubMessage[] {
  const norm = normalizeContactIdForQueue(contactId);
  if (!norm) return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data: Record<string, hubMessage[]> = JSON.parse(raw);
    const matchingKeys = Object.keys(data).filter((k) => k.toLowerCase() === norm);
    if (matchingKeys.length === 0) return [];
    const merged: hubMessage[] = [];
    const seen = new Set<string>();
    for (const key of matchingKeys) {
      for (const m of data[key] || []) {
        const id = normalizeHubMsgId(m);
        if (id && !seen.has(id)) {
          seen.add(id);
          merged.push(m);
        }
      }
      delete data[key];
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return merged;
  } catch {
    return [];
  }
}

/** Map persisted hub payload to ChatMain conversation row (matches handleIncomingMessage shape). */
export function mapQueuedHubToConversationMessage(message: hubMessage): Message {
  const msgId = normalizeHubMsgId(message);
  const currentUserId = typeof localStorage !== 'undefined' ? localStorage.getItem('UserId') : null;
  const isOutgoingMessage =
    normalizeContactIdForQueue(message.creatorId) === normalizeContactIdForQueue(currentUserId);

  return {
    id: msgId,
    msgId,
    parentMessageId: message.parentMessageId,
    creatorId: message.creatorId,
    type: message.type,
    messageBody: message.messageBody,
    docData: message.docData,
    createdDate: message.createdDate,
    firstName: message.firstName,
    lastName: message.lastName,
    email: message.email,
    userAvatar: message.userAvatar,
    isRead: false,
    isFromUser: isOutgoingMessage,
    isFromSignalR: true,
    parentMsgDta: message.parentMsgDta
      ? {
          id: message.parentMsgDta.id,
          msgId: message.parentMsgDta.msgId,
          parentMessageId: message.parentMsgDta.parentMessageId,
          creatorId: message.parentMsgDta.creatorId,
          creatorFirstName: message.parentMsgDta.creatorFirstName,
          creatorLastName: message.parentMsgDta.creatorLastName,
          creatorEmail: message.parentMsgDta.creatorEmail,
          type: message.parentMsgDta.type,
          messageBody: message.parentMsgDta.messageBody,
          docData: message.parentMsgDta.docData,
          createdDate: message.parentMsgDta.createdDate,
        }
      : null,
  };
}
